import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/axiosConfig';
import { useLanguage } from '../i18n/LanguageContext';
import './SearchAutocomplete.css';

interface TagSuggestion {
  id: number;
  name: string;
  color: string;
  text_color: string;
}

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

// Flattened dropdown item, so keyboard navigation can cross both sections.
type Item =
  | { kind: 'tag'; tag: TagSuggestion }
  | { kind: 'history'; query: string };

/**
 * Search box that suggests matching tags and past searches as the user types.
 * Tags come first (structured, color-chipped); recent free-text searches
 * follow. Picking either fills the box and runs the normal search, which
 * already matches tag names as well as content.
 */
const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  value,
  onChange,
  placeholder,
  className,
}) => {
  const { t } = useLanguage();
  const [tags, setTags] = useState<TagSuggestion[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const focused = useRef(false);
  const fetchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const items: Item[] = [
    ...tags.map((tag) => ({ kind: 'tag' as const, tag })),
    ...history.map((query) => ({ kind: 'history' as const, query })),
  ];

  const record = useCallback((query: string) => {
    const q = query.trim();
    if (q.length < 2) return; // don't clutter history with single characters
    api.post('/search-history/', { query: q }).catch(() => {});
  }, []);

  const fetchSuggestions = useCallback((raw: string) => {
    const query = raw.trim();
    const tagReq = query
      ? api.get('/tags/search/', { params: { q: query } })
      : Promise.resolve({ data: [] });
    const histReq = api.get('/search-history/', {
      params: query ? { q: query } : {},
    });

    Promise.all([tagReq, histReq])
      .then(([tagRes, histRes]) => {
        if (!focused.current) return;
        const tagList: TagSuggestion[] = tagRes.data || [];
        const tagNames = new Set(tagList.map((tag) => tag.name.toLowerCase()));
        const lower = query.toLowerCase();
        const histList: string[] = (histRes.data?.results || []).filter(
          // Drop history that just duplicates a shown tag or the exact input.
          (h: string) => !tagNames.has(h.toLowerCase()) && h.toLowerCase() !== lower
        );
        setTags(tagList);
        setHistory(histList);
        setActive(-1);
        setOpen(tagList.length + histList.length > 0);
      })
      .catch(() => {});
  }, []);

  // Debounced suggestion fetch as the value changes while focused.
  useEffect(() => {
    if (!focused.current) return;
    if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => fetchSuggestions(value), 150);
    return () => {
      if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
    };
  }, [value, fetchSuggestions]);

  // Record the query once typing settles (~1s), so a search is saved even if
  // the user never presses Enter and navigates away without blurring.
  useEffect(() => {
    if (!focused.current) return;
    if (recordTimeout.current) clearTimeout(recordTimeout.current);
    const q = value;
    recordTimeout.current = setTimeout(() => record(q), 1000);
    return () => {
      if (recordTimeout.current) clearTimeout(recordTimeout.current);
    };
  }, [value, record]);

  // Close when clicking outside.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (item: Item) => {
    if (item.kind === 'tag') {
      onChange(item.tag.name);
    } else {
      onChange(item.query);
      record(item.query);
    }
    setOpen(false);
    setActive(-1);
  };

  const removeHistory = (query: string) => {
    api.delete('/search-history/', { params: { q: query } }).catch(() => {});
    setHistory((prev) => prev.filter((x) => x !== query));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || items.length === 0) {
      if (e.key === 'Enter') record(value);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (e.key === 'Enter') {
      if (active >= 0 && active < items.length) {
        e.preventDefault();
        pick(items[active]);
      } else {
        record(value);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  // active index maps into the flat items array (tags first, then history)
  const historyOffset = tags.length;

  return (
    <div className={`search-autocomplete ${value ? 'has-value' : ''}`} ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          focused.current = true;
          fetchSuggestions(value);
        }}
        onBlur={() => {
          focused.current = false;
          record(value);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls="search-suggestions-list"
        aria-autocomplete="list"
      />
      {value && (
        <button
          type="button"
          className="search-clear"
          aria-label={t.home.clearSearch}
          title={t.home.clearSearch}
          // mousedown so the input keeps focus and the recent-searches menu
          // can come straight back up for the now-empty box
          onMouseDown={(e) => {
            e.preventDefault();
            onChange('');
            inputRef.current?.focus();
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      )}
      {open && items.length > 0 && (
        <ul id="search-suggestions-list" className="search-suggestions" role="listbox">
          {tags.length > 0 && (
            <li className="search-suggestion-heading" role="presentation">
              {t.home.suggestionsTags}
            </li>
          )}
          {tags.map((tag, i) => (
            <li
              key={`tag-${tag.id}`}
              role="option"
              aria-selected={i === active}
              className={`search-suggestion ${i === active ? 'active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick({ kind: 'tag', tag });
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span
                className="search-suggestion-chip"
                style={{ backgroundColor: tag.color, color: tag.text_color }}
              >
                {tag.name}
              </span>
            </li>
          ))}

          {history.length > 0 && (
            <li className="search-suggestion-heading" role="presentation">
              {t.home.suggestionsRecent}
            </li>
          )}
          {history.map((query, j) => {
            const idx = historyOffset + j;
            return (
              <li
                key={`hist-${query}`}
                role="option"
                aria-selected={idx === active}
                className={`search-suggestion ${idx === active ? 'active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick({ kind: 'history', query });
                }}
                onMouseEnter={() => setActive(idx)}
              >
                <svg
                  className="search-suggestion-icon"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="9" />
                  <polyline points="12 8 12 12 15 14" />
                </svg>
                <span className="search-suggestion-text">{query}</span>
                <button
                  type="button"
                  className="search-suggestion-remove"
                  aria-label={t.home.removeFromHistory.replace('{query}', query)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeHistory(query);
                  }}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SearchAutocomplete;
